/**
 * IA sidebar (US-AE.F0-0000-S04) — nhóm theo Epic (khớp prototype + docs/ADMIN_USECASES.md Phụ lục A).
 * labelKey/key trỏ i18n (g.x / n.x). badgeKey → QUEUE_COUNTS (thật: GET /v1/admin/queues/summary).
 */
import type { LucideIcon } from "lucide-react";
import {
  LayoutDashboard,
  ShieldCheck,
  History,
  Settings,
  ToggleRight,
  Languages,
  ArrowLeftRight,
  Users,
  ShieldQuestion,
  BadgeCheck,
  LayoutGrid,
  Scale,
  TriangleAlert,
  TrendingUp,
  RefreshCw,
  Gavel,
  Inbox,
  Siren,
  Flag,
  ScrollText,
  Megaphone,
  Send,
  RadioTower,
  FileText,
  Bot,
  MessagesSquare,
  SearchCheck,
  CreditCard,
  FlaskConical,
  ShieldAlert,
  Activity,
  Bell,
  ShieldX,
  ReceiptText,
  Database,
  KeyRound,
  BookMarked,
  Share2,
  Link2,
  Filter,
  Vote,
  Wallet,
  CalendarClock,
  GitPullRequest,
  MoonStar,
  Package,
  BarChart3,
  UploadCloud,
  Boxes,
  Workflow,
} from "lucide-react";

export interface NavItem {
  to: string;
  key: string;
  icon: LucideIcon;
  badgeKey?: string;
}
export interface NavGroup {
  labelKey: string;
  items: NavItem[];
}

export const NAV: NavGroup[] = [
  { labelKey: "g.overview", items: [{ to: "/", key: "n.dashboard", icon: LayoutDashboard }] },
  {
    labelKey: "g.ae1",
    items: [
      { to: "/admins", key: "n.admins", icon: ShieldCheck },
      // US-AE-IMP.F4-0114 (@UC9144) — allowlist domain admin (khống chế login admin).
      { to: "/allowlist", key: "n.allowlist", icon: ShieldQuestion },
      // @UC9002 — ma trận phân quyền UI (SUPER_ADMIN chỉnh nav visibility + action).
      { to: "/rbac-matrix", key: "n.rbac_matrix", icon: Scale },
      { to: "/audit-log", key: "n.audit_action", icon: History },
    ],
  },
  {
    labelKey: "g.ae2",
    items: [
      { to: "/settings", key: "n.settings", icon: Settings },
      { to: "/flags", key: "n.flags", icon: ToggleRight },
      { to: "/locale", key: "n.locale", icon: Languages },
      { to: "/config-overrides", key: "n.overrides", icon: ArrowLeftRight },
    ],
  },
  {
    labelKey: "g.ae3",
    items: [
      { to: "/users", key: "n.users", icon: Users },
      { to: "/gdpr", key: "n.gdpr", icon: ShieldQuestion, badgeKey: "gdpr" },
      { to: "/identity", key: "n.identity", icon: BadgeCheck },
    ],
  },
  {
    labelKey: "g.ae4",
    items: [
      { to: "/groups", key: "n.groups", icon: LayoutGrid },
      { to: "/funds", key: "n.funds", icon: Wallet },
      { to: "/member-imports", key: "n.member_imports", icon: UploadCloud },
    ],
  },
  {
    labelKey: "g.ae5",
    items: [
      { to: "/reconcile", key: "n.reconcile", icon: Scale },
      // US-E15.F6-0181 (@UC9227-9230) — đối soát nợ + netting graph + can thiệp tất toán/xoá nợ.
      { to: "/debts", key: "n.debts", icon: ArrowLeftRight },
      { to: "/drift", key: "n.drift", icon: TriangleAlert, badgeKey: "drift" },
      { to: "/cashflow", key: "n.cashflow", icon: TrendingUp },
      { to: "/idempotency", key: "n.idempotency", icon: RefreshCw },
    ],
  },
  {
    labelKey: "g.ae6",
    items: [{ to: "/payments", key: "n.payments", icon: CreditCard }],
  },
  {
    labelKey: "g.ae10",
    items: [
      { to: "/collections", key: "n.collections", icon: Wallet },
      // 🔴 US-AE16.F2-0370 (@UC9635 @UC9636) — lịch thu định kỳ = NGUỒN SINH nghĩa vụ tiền. Đây là
      // màn duy nhất nhìn thấy + tắt được nó khi nhóm đã đóng sổ mà worker vẫn chạy (bug #6).
      { to: "/collection-schedules", key: "n.collectionSchedules", icon: CalendarClock },
      // US-AE5.F6-0093 (@UC9164) — danh sách TOÀN BỘ khoản chi xuyên quỹ (read-only oversight).
      { to: "/expenses", key: "n.expenses", icon: ReceiptText },
      // US-E20.F6-0212 (@UC9343–@UC9345) — 5 cỗ máy chuyên biệt (hụi · hiếu hỉ · earmark · NAV · ghế).
      { to: "/machines", key: "n.machines", icon: Boxes },
      { to: "/voting", key: "n.voting", icon: Vote },
    ],
  },
  {
    labelKey: "g.ae11",
    items: [
      { to: "/disputes", key: "n.disputes", icon: Gavel, badgeKey: "disputes" },
      { to: "/corrections", key: "n.corrections", icon: Scale, badgeKey: "corrections" },
      { to: "/inbox", key: "n.inbox", icon: Inbox },
    ],
  },
  {
    labelKey: "g.ae12",
    items: [
      { to: "/risk", key: "n.risk", icon: Siren, badgeKey: "risk" },
      { to: "/abuse", key: "n.abuse", icon: Flag, badgeKey: "abuse" },
      { to: "/moderation", key: "n.moderation", icon: ScrollText },
    ],
  },
  {
    labelKey: "g.ae12b",
    items: [
      { to: "/transparency", key: "n.transparency", icon: Link2 },
      { to: "/referrals", key: "n.referrals", icon: Share2 },
      { to: "/onboarding", key: "n.onboarding", icon: Filter },
      { to: "/analytics/growth", key: "n.analyticsGrowth", icon: TrendingUp },
      { to: "/analytics/referrals", key: "n.analyticsReferrals", icon: TrendingUp },
      { to: "/analytics/group-health", key: "n.analyticsGroupHealth", icon: TrendingUp },
    ],
  },
  {
    labelKey: "g.ae13",
    items: [
      { to: "/broadcast", key: "n.broadcast", icon: Megaphone },
      // US-AE13.F2-0045 (@UC9122) — cấu hình khung giờ yên lặng VN (hoãn push/ZNS).
      { to: "/quiet-hours", key: "n.quietHours", icon: MoonStar },
    ],
  },
  {
    labelKey: "g.ae7",
    items: [
      { to: "/outbox", key: "n.outbox", icon: Send },
      { to: "/channels", key: "n.channels", icon: RadioTower },
      { to: "/templates", key: "n.templates", icon: FileText },
    ],
  },
  {
    labelKey: "g.ae8",
    items: [
      // US-AI-0294 (@UC9417 · PO chốt 21/07) — "Cấu hình AI" đứng TRƯỚC "Orchestration AI": đây là
      // nơi bật/tắt AI (3 tầng kill-switch, thao tác tại chỗ), phải thấy trước khi cấu hình flow.
      { to: "/ai/kill-switch", key: "n.ai_killswitch", icon: ShieldCheck },
      // US-AI.F5-0234 (@UC9400 @AI-TRU-01): MỞ LẠI "/ai/orchestration" — backend config flow
      // (US-AI.F3-0232) + preview engine thật (US-AI.F4-0233) đã có ⇒ trang không còn mock (F8 gỡ vì
      // toggle giả; nay PATCH thật + preview chạy engine thật trên sandbox).
      { to: "/ai/orchestration", key: "n.ai_orch", icon: Workflow },
      { to: "/ai/chats", key: "n.ai_chats", icon: MessagesSquare },
      { to: "/ai/models", key: "n.ai_models", icon: Bot },
      { to: "/ai/audit", key: "n.ai_audit", icon: SearchCheck },
      { to: "/ai/cost", key: "n.ai_cost", icon: CreditCard },
      { to: "/ai/eval", key: "n.ai_eval", icon: FlaskConical },
      { to: "/ai/safety", key: "n.ai_safety", icon: ShieldAlert },
      // US-AI-0310-S06 (@UC9400 · @gate config) — config deep-link AI theo iOS/Android/Web (5 field).
      { to: "/ai/deep-links", key: "n.ai_deeplinks", icon: Link2 },
      // US-AE14.F1-0046 (@UC9130) — danh mục gói & subscription (monetization).
      { to: "/billing", key: "n.billing", icon: Package },
      // US-AE14.F2-0047 (@UC9131/@UC9133) — usage metering & unit economics (read-only).
      { to: "/billing/usage", key: "n.usageEconomics", icon: BarChart3 },
    ],
  },
  {
    labelKey: "g.ae9",
    items: [
      { to: "/ci", key: "n.ci", icon: GitPullRequest },
      { to: "/metrics", key: "n.metrics", icon: Activity },
      { to: "/alerts", key: "n.alerts", icon: Bell },
      { to: "/threats", key: "n.threats", icon: ShieldX },
      // 🔴 US-E19.F7-0206 (@UC9320) — console an ninh: quỹ đóng băng · cảnh báo hành vi · chi trong
      // cửa sổ nguội · audit; can thiệp (freeze/huỷ/đóng case) tách khỏi phần đọc.
      { to: "/security-console", key: "n.securityConsole", icon: ShieldAlert },
      { to: "/audit-viewer", key: "n.auditlog", icon: ReceiptText },
      { to: "/backup", key: "n.backup", icon: Database },
      { to: "/secrets", key: "n.secrets", icon: KeyRound },
    ],
  },
  {
    labelKey: "g.dict",
    items: [
      { to: "/dict/income-types", key: "n.d_income", icon: BookMarked },
      { to: "/dict/expense-categories", key: "n.d_expense", icon: BookMarked },
      { to: "/dict/chart-of-accounts", key: "n.d_coa", icon: BookMarked },
      { to: "/dict/enum-registry", key: "n.d_enum", icon: BookMarked },
      { to: "/dict/error-catalog", key: "n.d_error", icon: BookMarked },
      { to: "/dict/blocklist", key: "n.d_blocklist", icon: BookMarked },
      { to: "/dict/reason-codes", key: "n.d_reason", icon: BookMarked },
      { to: "/dict/referral-channels", key: "n.d_refch", icon: BookMarked },
      { to: "/dict/banks", key: "n.d_banks", icon: BookMarked },
    ],
  },
];

/** Mọi item `to` (phẳng) — dùng cho helper active/breadcrumb. */
const ALL_ITEMS: NavItem[] = NAV.flatMap((g) => g.items);

/**
 * `to` là prefix-SEGMENT của một item NAV khác? (vd '/billing' của '/billing/usage').
 *
 * Bug rev2 #29a: NavLink prefix-match khiến '/billing' active khi ở '/billing/usage'.
 * Item nào là prefix-lồng của item khác phải dùng NavLink `end` (exact) để không active nhầm;
 * item còn lại giữ prefix-match để route con động (vd '/users/:id') vẫn sáng ở cha.
 * Khớp ranh giới segment (`${to}/`) — KHÔNG substring thô, tránh '/ai/audit' ⊂ '/ai/audit-viewer'.
 */
export function navItemNeedsEnd(to: string): boolean {
  const prefix = `${to}/`;
  return ALL_ITEMS.some((it) => it.to !== to && it.to.startsWith(prefix));
}

/**
 * Suy nhóm epic + trang từ pathname theo NAV (longest-match → item con, không dừng ở cha ngắn).
 *
 * Bug rev2 #29a (breadcrumb): startsWith + break ở first-hit khớp cha '/billing' trước con
 * '/billing/usage'. Sửa: chọn item có `to` khớp DÀI NHẤT (exact hoặc segment-prefix của pathname).
 * '/' chỉ khớp khi pathname === '/' (không là prefix-lồng của mọi route).
 */
export function resolveCrumbKeys(pathname: string): { group: string; pageKey: string } {
  let best: { group: string; item: NavItem } | undefined;
  for (const g of NAV) {
    for (const it of g.items) {
      const matches =
        it.to === pathname ||
        (it.to !== "/" && (pathname === it.to || pathname.startsWith(`${it.to}/`)));
      if (matches && (!best || it.to.length > best.item.to.length)) {
        best = { group: g.labelKey, item: it };
      }
    }
  }
  return best
    ? { group: best.group, pageKey: best.item.key }
    : { group: "g.overview", pageKey: "n.dashboard" };
}
